import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/apiClient';
import downloadFile from '../../utils/fileDownloader';
import { toast } from 'sonner';
import { Upload, Calendar, Plus, Trash2, FileText, Download } from 'lucide-react';
import { useAuth } from '@/App';

const HolidayManagement = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    holiday_type: 'School',
    total_days: 1
  });

  const {user} = useAuth();
  const hostelId = user?.hostel_id || user?.assigned_hostel_id || user?.hostelId || localStorage.getItem('hostel_id') || '';

  useEffect(() => {
    fetchHolidays();
  }, [hostelId]);

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const url = hostelId ? `/v1/admin/holidays/?hostel_id=${hostelId}` : '/v1/admin/holidays/';
      const response = await apiClient.get(url);
      setHolidays(response.data.holidays || []);
    } catch (error) {
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = fileType === 'pdf' ? ['application/pdf'] : ['text/csv', 'application/vnd.ms-excel'];
    if (!validTypes.includes(file.type) && !file.name.endsWith(`.${fileType}`)) {
      toast.error(`Please upload a valid ${fileType.toUpperCase()} file`);
      return;
    }

    setUploading(true);
    const formDataObj = new FormData();
    formDataObj.append('file', file);
    if (hostelId) {
      formDataObj.append('hostel_id', hostelId);
    }

    try {
      const endpoint = fileType === 'pdf' 
        ? '/v1/admin/holidays/upload-pdf'
        : '/v1/admin/holidays/upload-csv';
      
      const response = await apiClient.post(endpoint, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`✅ ${response.data.created_count || 0} holidays imported successfully!`);
      fetchHolidays();
    } catch (error) {
      console.error('File upload holiday error:', error);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = { ...formData };
      if (hostelId) {
        payload.hostel_id = hostelId;
      }
      await apiClient.post('/v1/admin/holidays/', payload);

      toast.success('Holiday created successfully!');
      setShowManualModal(false);
      resetForm();
      fetchHolidays();
    } catch (error) {
      console.error('Manual submit holiday error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (holidayId) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;

    try {
      await apiClient.delete(`/v1/admin/holidays/${holidayId}`);
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      start_date: '',
      end_date: '',
      holiday_type: 'School',
      total_days: 1
    });
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 1;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts[0].length === 4) {
        const [y, m, d] = parts;
        const dateObj = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        return dateObj.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
      } else if (parts[2]?.length === 4) {
        const [d, m, y] = parts;
        const dateObj = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        return dateObj.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
      }
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getHolidayTypeColor = (type) => {
    switch(type) {
      case 'National': return 'bg-red-100 text-red-700';
      case 'School': return 'bg-blue-100 text-blue-700';
      case 'Optional': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const downloadSampleCSV = () => {
    const csvContent = `name,start_date,end_date,holiday_type,total_days
Republic Day,2024-01-26,2024-01-26,National,1
Summer Vacation,2024-05-01,2024-06-15,School,46
Diwali,2024-10-31,2024-11-03,National,4`;
    
    downloadFile(csvContent, 'holiday_template.csv', 'text/csv');
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#002D5B' }}>Holiday Management</h1>
        <p className="text-gray-600 mt-1">Manage academic calendar and holidays</p>
      </div>

      {/* Upload Section */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {/* Manual Add */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center w-12 h-12 rounded-full mb-4 mx-auto"
               style={{ backgroundColor: '#E6F2FF' }}>
            <Plus className="w-6 h-6" style={{ color: '#5A9FD4' }} />
          </div>
          <h3 className="text-center font-semibold mb-2">Add Manually</h3>
          <p className="text-sm text-gray-600 text-center mb-4">Create individual holidays one at a time</p>
          <button
            onClick={() => setShowManualModal(true)}
            className="w-full py-2 text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: '#5A9FD4' }}
          >
            Add Holiday
          </button>
        </div>

        {/* CSV Upload */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center w-12 h-12 rounded-full mb-4 mx-auto"
               style={{ backgroundColor: '#E6F2FF' }}>
            <FileText className="w-6 h-6" style={{ color: '#5A9FD4' }} />
          </div>
          <h3 className="text-center font-semibold mb-2">Upload CSV</h3>
          <p className="text-sm text-gray-600 text-center mb-4">Bulk import from CSV file</p>
          <input
            type="file"
            accept=".csv"
            id="csv-upload"
            className="hidden"
            onChange={(e) => handleFileUpload(e, 'csv')}
            disabled={uploading}
          />
          <label
            htmlFor="csv-upload"
            className={`block w-full py-2 text-white rounded-lg text-center cursor-pointer hover:opacity-90 ${
              uploading ? 'bg-gray-400' : ''
            }`}
            style={{ backgroundColor: uploading ? undefined : '#5A9FD4' }}
          >
            {uploading ? 'Uploading...' : 'Choose CSV File'}
          </label>
          <button
            onClick={downloadSampleCSV}
            className="w-full mt-2 py-1 text-xs text-blue-600 hover:underline flex items-center justify-center gap-1"
          >
            <Download className="w-3 h-3" />
            Download Sample CSV
          </button>
        </div>

        {/* PDF Upload */}
        {/* <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center w-12 h-12 rounded-full mb-4 mx-auto"
               style={{ backgroundColor: '#E6F2FF' }}>
            <Upload className="w-6 h-6" style={{ color: '#5A9FD4' }} />
          </div>
          <h3 className="text-center font-semibold mb-2">Upload PDF</h3>
          <p className="text-sm text-gray-600 text-center mb-4">Extract holidays from PDF document</p>
          <input
            type="file"
            accept=".pdf"
            id="pdf-upload"
            className="hidden"
            onChange={(e) => handleFileUpload(e, 'pdf')}
            disabled={uploading}
          />
          <label
            htmlFor="pdf-upload"
            className={`block w-full py-2 text-white rounded-lg text-center cursor-pointer hover:opacity-90 ${
              uploading ? 'bg-gray-400' : ''
            }`}
            style={{ backgroundColor: uploading ? undefined : '#5A9FD4' }}
          >
            {uploading ? 'Uploading...' : 'Choose PDF File'}
          </label>
        </div> */}
      </div>

      {/* Holidays List */}
      {loading ? (
        <div className="text-center py-8" style={{ color: '#002D5B' }}>Loading holidays...</div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ backgroundColor: '#F8FBFD' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ color: '#002D5B' }}>
                    Holiday Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ color: '#002D5B' }}>
                    Start Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ color: '#002D5B' }}>
                    End Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ color: '#002D5B' }}>
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ color: '#002D5B' }}>
                    Days
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider"
                      style={{ color: '#002D5B' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {holidays.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No holidays added yet. Upload a CSV/PDF or add manually.
                    </td>
                  </tr>
                ) : (
                  holidays.map((holiday) => (
                    <tr key={holiday.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <Calendar className="w-5 h-5 mr-2" style={{ color: '#5A9FD4' }} />
                          <span className="font-medium text-gray-900">{holiday.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {formatDateDisplay(holiday.start_date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {formatDateDisplay(holiday.end_date)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded ${getHolidayTypeColor(holiday.holiday_type)}`}>
                          {holiday.holiday_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {holiday.total_days} {holiday.total_days === 1 ? 'day' : 'days'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleDelete(holiday.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Manual Add Modal */}
      {showManualModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002D5B' }}>Add Holiday</h2>

            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Holiday Name *</label>
                <input
                  type="text"
                  required
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Independence Day"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                  <input
                    type="date"
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={formData.start_date}
                    onChange={(e) => {
                      const newDays = calculateDays(e.target.value, formData.end_date);
                      setFormData({...formData, start_date: e.target.value, total_days: newDays});
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date *</label>
                  <input
                    type="date"
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    value={formData.end_date}
                    min={formData.start_date}
                    onChange={(e) => {
                      const newDays = calculateDays(formData.start_date, e.target.value);
                      setFormData({...formData, end_date: e.target.value, total_days: newDays});
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Holiday Type *</label>
                <select
                  required
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={formData.holiday_type}
                  onChange={(e) => setFormData({...formData, holiday_type: e.target.value})}
                >
                  <option value="National">National</option>
                  <option value="School">School</option>
                  <option value="Optional">Optional</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Total Days</label>
                <input
                  type="number"
                  min="1"
                  className="w-full p-2 border border-gray-300 rounded-lg bg-gray-50"
                  value={formData.total_days}
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">Auto-calculated from dates</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowManualModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:bg-gray-400"
                  style={{ backgroundColor: '#5A9FD4' }}
                >
                  {loading ? 'Creating...' : 'Create Holiday'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HolidayManagement;
